/**
 * Parses an ISO 8601 duration string and returns an object with duration components
 * Supports full ISO 8601 duration format: P[n]Y[n]M[n]DT[n]H[n]M[n]S
 * Examples:
 * - "P2Y3M" -> { years: 2, months: 3, days: 0, hours: 0, minutes: 0, seconds: 0 }
 * - "P1Y2M3DT4H5M6S" -> { years: 1, months: 2, days: 3, hours: 4, minutes: 5, seconds: 6 }
 * - "PT30M" -> { years: 0, months: 0, days: 0, hours: 0, minutes: 30, seconds: 0 }
 * - "P48M" with normalize=true -> { years: 4, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 }
 *
 * @param {string} iso8601duration - ISO 8601 duration string
 * @param {boolean} normalize - If true, converts larger units (e.g., 48 months -> 4 years)
 * @returns {Object} Duration object with years, months, days, hours, minutes, seconds
 */
const parseISODuration = (iso8601duration, normalize = false) => {
  if (!iso8601duration || typeof iso8601duration !== 'string') {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  // ISO 8601 duration regex pattern
  // P[n]Y[n]M[n]DT[n]H[n]M[n]S
  //
  // Breaking down the regex components:
  // ^P                                    - Start with required 'P' prefix
  // (?:(\d+(?:\.\d+)?)Y)?                 - Optional years: digits (with optional decimal) followed by 'Y'
  // (?:(\d+(?:\.\d+)?)M)?                 - Optional months: digits (with optional decimal) followed by 'M' (before T)
  // (?:(\d+(?:\.\d+)?)D)?                 - Optional days: digits (with optional decimal) followed by 'D'
  // (?:T                                  - Optional time section starts with 'T'
  //   (?:(\d+(?:\.\d+)?)H)?               - Optional hours: digits (with optional decimal) followed by 'H'
  //   (?:(\d+(?:\.\d+)?)M)?               - Optional minutes: digits (with optional decimal) followed by 'M' (after T)
  //   (?:(\d+(?:\.\d+)?)S)?               - Optional seconds: digits (with optional decimal) followed by 'S'
  // )?                                    - End optional time section
  // $                                     - End of string
  //
  // Capture groups (in order):
  // 1: Years value
  // 2: Months value
  // 3: Days value
  // 4: Hours value
  // 5: Minutes value
  // 6: Seconds value
  //
  // Note: \d+(?:\.\d+)? matches integers or decimals (e.g., "1", "1.5", "10.25")
  const regex =
    /^P(?:(\d+(?:\.\d+)?)Y)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

  const match = iso8601duration.match(regex);

  if (!match) {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const duration = {
    years: parseFloat(match[1] || 0),
    months: parseFloat(match[2] || 0),
    days: parseFloat(match[3] || 0),
    hours: parseFloat(match[4] || 0),
    minutes: parseFloat(match[5] || 0),
    seconds: parseFloat(match[6] || 0),
  };

  // If normalize is true, convert larger units to smaller ones
  if (normalize) {
    // Convert months to years (12 months = 1 year)
    if (duration.months >= 12) {
      const additionalYears = Math.floor(duration.months / 12);
      duration.years += additionalYears;
      duration.months %= 12;
    }

    // Convert hours to days (24 hours = 1 day)
    if (duration.hours >= 24) {
      const additionalDays = Math.floor(duration.hours / 24);
      duration.days += additionalDays;
      duration.hours %= 24;
    }

    // Convert minutes to hours (60 minutes = 1 hour)
    if (duration.minutes >= 60) {
      const additionalHours = Math.floor(duration.minutes / 60);
      duration.hours += additionalHours;
      duration.minutes %= 60;

      // Check again if hours need to be converted to days
      if (duration.hours >= 24) {
        const additionalDays = Math.floor(duration.hours / 24);
        duration.days += additionalDays;
        duration.hours %= 24;
      }
    }

    // Convert seconds to minutes (60 seconds = 1 minute)
    if (duration.seconds >= 60) {
      const additionalMinutes = Math.floor(duration.seconds / 60);
      duration.minutes += additionalMinutes;
      duration.seconds %= 60;

      // Check again if minutes need to be converted to hours/days
      if (duration.minutes >= 60) {
        const additionalHours = Math.floor(duration.minutes / 60);
        duration.hours += additionalHours;
        duration.minutes %= 60;

        if (duration.hours >= 24) {
          const additionalDays = Math.floor(duration.hours / 24);
          duration.days += additionalDays;
          duration.hours %= 24;
        }
      }
    }
  }

  return duration;
};

export default parseISODuration;
